import { ArrowRight, ArrowLeftRight, ChevronDown, Calendar, Clock, ArrowDownUp, Navigation2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import DateTimePicker, { type DateTimeValue, formatDateLabel, formatTimeLabel } from '../../../../components/DateTimePicker';
import { DashboardLocationInput } from '../components/DashboardLocationInput';
import { isLocationInSalzburg, type LocationGeoData } from '../../../../utils/salzburgLocation';
import { calculateRouteDistanceAndTiming } from '../../../../services/googleRouteCalculation';
import { HOURLY_DURATION_OPTIONS } from '../../../../constants/hourlyBooking';
import {
  apiGetDayTrips,
  type DayTrip,
} from '../../../../api/dayTrips/dayTripApi';

export type TripDetailsData = {
  serviceType: string;
  dayTripId: string; // Selected day trip
  journeyType: 'one-way' | 'round-trip';
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: string;
  pickupTime: string;
  returnDate: string;
  returnTime: string;
  passengers: string;
  luggage: string;
  sourceName: string;
  pickupGeo?: LocationGeoData | null;
  dropoffGeo?: LocationGeoData | null;
  distanceKm?: number;
  durationHours?: number;
  durationMinutes?: number;
  distanceText?: string;
  durationText?: string;
  paymentStatus: 'pre_paid' | 'cash' | 'account_transfer' | 'paid';
};

type Step1Props = {
  data: TripDetailsData;
  updateData: (data: Partial<TripDetailsData>) => void;
};

export const Step1TripDetails = ({ data, updateData }: Step1Props) => {
  const [showDeparturePicker, setShowDeparturePicker] = useState(false);
  const [showReturnPicker, setShowReturnPicker] = useState(false);
  const departureRef = useRef<HTMLDivElement>(null);
  const returnRef = useRef<HTMLDivElement>(null);
  const [dayTrips, setDayTrips] = useState<DayTrip[]>([]);
  const [dayTripsLoading, setDayTripsLoading] = useState(false);
  const [dayTripsError, setDayTripsError] = useState('');

  useEffect(() => {
    if (data.serviceType !== 'Day trips') {
      setDayTrips([]);
      setDayTripsError('');
      setDayTripsLoading(false);
      return;
    }

    let cancelled = false;

    const loadDayTrips = async () => {
      setDayTripsLoading(true);
      setDayTripsError('');

      try {
        const response = await apiGetDayTrips();

        if (cancelled) return;

        if (response.success && Array.isArray(response.data)) {
          setDayTrips(
            response.data.filter((trip) => trip.active !== false),
          );
        } else {
          setDayTrips([]);
          setDayTripsError(
            response.message || 'Failed to load day trips.',
          );
        }
      } catch {
        if (!cancelled) {
          setDayTrips([]);
          setDayTripsError('Failed to load day trips. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setDayTripsLoading(false);
        }
      }
    };

    void loadDayTrips();

    return () => {
      cancelled = true;
    };
  }, [data.serviceType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departureRef.current && !departureRef.current.contains(event.target as Node)) {
        setShowDeparturePicker(false);
      }
      if (returnRef.current && !returnRef.current.contains(event.target as Node)) {
        setShowReturnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const departureValue: DateTimeValue | null = data.pickupDate
    ? { date: data.pickupDate, time: data.pickupTime || '09:00' }
    : null;

  const returnValue: DateTimeValue | null = data.returnDate
    ? { date: data.returnDate, time: data.returnTime || '09:00' }
    : null;

  const isHourly = data.serviceType === 'Hourly drivers';

  // Salzburg mutual boundary validation matching frontend Hero search logic
  const isFromInSalzburg = isLocationInSalzburg(
    data.pickupGeo || { title: data.pickupLocation, address: data.pickupLocation }
  );

  const isToInSalzburg = isLocationInSalzburg(
    data.dropoffGeo || { title: data.dropoffLocation, address: data.dropoffLocation }
  );

  const isFromRestricted = isHourly || (!isToInSalzburg && Boolean(data.dropoffLocation));
  const isToRestricted = !isFromInSalzburg && Boolean(data.pickupLocation);

  // Auto-calculate driving distance & duration via Google Maps / resilient route calculation
  useEffect(() => {
    if (isHourly || !data.pickupLocation || !data.dropoffLocation) {
      return;
    }

    let isMounted = true;

    const calcDistance = async () => {
      try {
        const origin = {
          title: data.pickupLocation,
          address: data.pickupGeo?.address || data.pickupLocation,
          lat: data.pickupGeo?.lat,
          lng: data.pickupGeo?.lng,
        };
        const destination = {
          title: data.dropoffLocation,
          address: data.dropoffGeo?.address || data.dropoffLocation,
          lat: data.dropoffGeo?.lat,
          lng: data.dropoffGeo?.lng,
        };

        const result = await calculateRouteDistanceAndTiming(origin, destination);

        if (isMounted && result.distanceKm > 0) {
          updateData({
            distanceKm: result.distanceKm,
            distanceText: result.distanceText,
            durationText: result.durationText,
            durationHours: Math.max(1, Math.round(result.durationMinutes / 60)),
            durationMinutes: result.durationMinutes,
          });
        }
      } catch (err) {
        console.warn('Failed to calculate route distance:', err);
      }
    };

    calcDistance();

    return () => {
      isMounted = false;
    };
  }, [
    data.pickupLocation,
    data.dropoffLocation,
    data.pickupGeo?.lat,
    data.pickupGeo?.lng,
    data.dropoffGeo?.lat,
    data.dropoffGeo?.lng,
    data.serviceType,
  ]);

  const handleSwapLocations = () => {
    if (!data.pickupLocation && !data.dropoffLocation) return;
    updateData({
      pickupLocation: data.dropoffLocation,
      dropoffLocation: data.pickupLocation,
      pickupGeo: data.dropoffGeo,
      dropoffGeo: data.pickupGeo,
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1000px] mx-auto px-[32px]">
      <h2 className="font-host text-[18px] font-bold text-[#1A1A1A]">Trip Details</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Service Type */}
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">Service Type</label>
          <div className="relative">
            <select
              value={data.serviceType}
              onChange={(e) => {
                const nextType = e.target.value;

                if (nextType === 'Hourly drivers' && !data.durationHours) {
                  updateData({
                    serviceType: nextType,
                    dayTripId: '',
                    durationHours: 2,
                    distanceKm: 80,
                  });
                } else {
                  updateData({
                    serviceType: nextType,
                    ...(nextType !== 'Day trips' && { dayTripId: '' }),
                  });
                }
              }}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] appearance-none focus:outline-none focus:border-[#FF6A1A] cursor-pointer"
            >
              <option value="Transfers">Transfers</option>
              <option value="Hourly drivers">Hourly drivers</option>
              <option value="Day trips">Day trips</option>
            </select>

            <ChevronDown
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]"
              size={16}
            />
          </div>
        </div>
        {/* Day Trip Selection — only for Day trips */}
        {data.serviceType === 'Day trips' && (
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">
              Select Day Trip <span className="text-[#FF6A1A]">*</span>
            </label>

            <div className="relative">
              <select
                value={data.dayTripId}
                onChange={(e) => updateData({ dayTripId: e.target.value })}
                disabled={dayTripsLoading || Boolean(dayTripsError)}
                className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 pr-10 font-host text-[14px] text-[#1A1A1A] appearance-none focus:outline-none focus:border-[#FF6A1A] cursor-pointer disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
              >
                <option value="">
                  {dayTripsLoading
                    ? 'Loading day trips...'
                    : dayTripsError
                      ? 'Unable to load day trips'
                      : dayTrips.length === 0
                        ? 'No day trips available'
                        : 'Select a day trip'}
                </option>

                {dayTrips.map((trip) => (
                  <option key={trip.id} value={trip.id}>
                    {trip.title}
                  </option>
                ))}
              </select>

              <ChevronDown
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A] pointer-events-none"
                size={16}
              />
            </div>

            {dayTripsError && (
              <p className="text-[12px] text-red-600">
                {dayTripsError}
              </p>
            )}
          </div>
        )}

        {/* 👇 PUT SOURCE BLOCK HERE */}
        {['Transfers', 'Day trips', 'Hourly drivers'].includes(data.serviceType) && (
          <>
            {/* Source Number */}
            {/* <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#757575]">
                Source Number <span className="text-[#FF6A1A]">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter source number"
                value={data.sourceNumber}
                onChange={(e) =>
                  updateData({ sourceNumber: e.target.value })
                }
                className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
              />
            </div> */}

            {/* Source Name */}
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#757575]">
                Source Name <span className="text-[#FF6A1A]">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter source name"
                value={data.sourceName}
                onChange={(e) =>
                  updateData({ sourceName: e.target.value })
                }
                className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
              />
            </div>
          </>
        )}

        {/* Journey Type */}
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Journey Type <span className="text-[#FF6A1A]">*</span>
          </label>
          <div className="flex items-center gap-2 h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white p-1">
            <button
              type="button"
              onClick={() => updateData({ journeyType: 'one-way' })}
              className={`flex-1 flex items-center justify-center gap-2 h-full rounded-[6px] font-host text-[13px] font-bold transition-colors ${
                data.journeyType === 'one-way'
                  ? 'bg-[#3B5998] text-white uppercase'
                  : 'bg-transparent text-[#9E9E9E] hover:bg-[#F5F5F5]'
              }`}
            >
              <div className={`flex items-center justify-center size-[20px] rounded-full ${data.journeyType === 'one-way' ? 'bg-white text-[#3B5998]' : 'bg-[#E0E0E0] text-[#9E9E9E]'}`}>
                <ArrowRight size={12} />
              </div>
              ONE WAY
            </button>
            <button
              type="button"
              onClick={() => updateData({ journeyType: 'round-trip' })}
              className={`flex-1 flex items-center justify-center gap-2 h-full rounded-[6px] font-host text-[13px] font-bold transition-colors ${
                data.journeyType === 'round-trip'
                  ? 'bg-[#3B5998] text-white uppercase'
                  : 'bg-transparent text-[#9E9E9E] hover:bg-[#F5F5F5]'
              }`}
            >
              <div className={`flex items-center justify-center size-[20px] rounded-full ${data.journeyType === 'round-trip' ? 'bg-white text-[#3B5998]' : 'bg-[#E0E0E0] text-[#9E9E9E]'}`}>
                <ArrowLeftRight size={12} />
              </div>
              Round Trip
            </button>
          </div>
        </div>

        {/* Pickup Location - Location search same as Hero section */}
        <DashboardLocationInput
          label="Pickup Location"
          required
          value={data.pickupLocation}
          geoValue={data.pickupGeo}
          placeholder="Search pickup address, hotel or airport..."
          isRestrictedToSalzburg={isFromRestricted}
          onSelect={(location) => {
            updateData({
              pickupLocation: location.title,
              pickupGeo: location,
            });
          }}
          onChangeText={(text) => {
            updateData({
              pickupLocation: text,
              pickupGeo: text ? data.pickupGeo : null,
            });
          }}
          onClear={() => {
            updateData({
              pickupLocation: '',
              pickupGeo: null,
              distanceKm: 0,
            });
          }}
        />

        {/* Drop-off Location or Duration for Hourly */}
        {isHourly ? (
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">
              Duration (Hours & Distance) <span className="text-[#FF6A1A]">*</span>
            </label>
            <div className="relative">
              <select
                value={data.durationHours || 2}
                onChange={(e) => {
                  const hours = Number(e.target.value);
                  const opt = HOURLY_DURATION_OPTIONS.find((o) => o.hours === hours);
                  updateData({
                    durationHours: hours,
                    distanceKm: opt?.km || hours * 40,
                  });
                }}
                className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] appearance-none focus:outline-none focus:border-[#FF6A1A] cursor-pointer"
              >
                {HOURLY_DURATION_OPTIONS.map((opt) => (
                  <option key={opt.hours} value={opt.hours}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]" size={16} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 relative">
            <DashboardLocationInput
              label="Drop-off Location"
              required
              value={data.dropoffLocation}
              geoValue={data.dropoffGeo}
              placeholder="Search drop-off address, hotel or airport..."
              isRestrictedToSalzburg={isToRestricted}
              onSelect={(location) => {
                updateData({
                  dropoffLocation: location.title,
                  dropoffGeo: location,
                });
              }}
              onChangeText={(text) => {
                updateData({
                  dropoffLocation: text,
                  dropoffGeo: text ? data.dropoffGeo : null,
                });
              }}
              onClear={() => {
                updateData({
                  dropoffLocation: '',
                  dropoffGeo: null,
                  distanceKm: 0,
                });
              }}
            />
          </div>
        )}

        {/* Location Swap & Route Summary Banner */}
        {!isHourly && (data.pickupLocation || data.dropoffLocation) && (
          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 p-3 rounded-[12px] bg-[#F8FAFC] border border-[#E2E8F0]">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSwapLocations}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-[#CBD5E1] text-[12px] font-host font-semibold text-[#334155] hover:bg-[#F1F5F9] transition-colors shadow-xs"
                title="Swap Pickup & Drop-off locations"
              >
                <ArrowDownUp size={14} className="text-[#3B5998]" />
                Swap Locations
              </button>

              {Boolean(data.distanceKm && data.distanceKm > 0) && (
                <div className="flex items-center gap-2 text-[13px] font-host text-[#1E293B]">
                  <Navigation2 size={15} className="rotate-45 text-[#FF6A1A]" />
                  <span className="font-bold">{data.distanceKm} km</span>
                  <span className="text-[#64748B]">
                    • Approx. {data.durationText || `${data.durationHours || 1} hrs`}
                  </span>
                  {data.journeyType === 'round-trip' && (
                    <span className="text-[#3B5998] font-semibold text-[11px] bg-[#EEF2FF] px-2 py-0.5 rounded-full">
                      Round trip calculated (2x)
                    </span>
                  )}
                </div>
              )}
            </div>

            {Boolean(data.distanceKm && data.distanceKm > 0) && (
              <span className="font-host text-[11px] font-medium text-[#15803D] bg-[#DCFCE7] px-2.5 py-0.5 rounded-full">
                Driving distance calculated
              </span>
            )}
          </div>
        )}

        {/* Pickup Date & Time */}
        <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1" ref={departureRef}>
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Pickup Date & Time <span className="text-[#FF6A1A]">*</span>
          </label>
          <div className="relative flex items-center h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white divide-x divide-[#E0E0E0]">
            <button
              type="button"
              onClick={() => {
                setShowDeparturePicker(!showDeparturePicker);
                setShowReturnPicker(false);
              }}
              className="flex-1 flex items-center justify-between px-4 h-full"
            >
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#9E9E9E]" />
                <span className="font-host text-[14px] text-[#1A1A1A]">
                  {formatDateLabel(data.pickupDate)}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDeparturePicker(!showDeparturePicker);
                setShowReturnPicker(false);
              }}
              className="w-[120px] flex items-center justify-center gap-2 h-full shrink-0"
            >
              <Clock size={16} className="text-[#9E9E9E]" />
              <span className="font-host text-[14px] text-[#1A1A1A]">
                {formatTimeLabel(departureValue)}
              </span>
            </button>
            
            {showDeparturePicker && (
              <DateTimePicker
                value={departureValue}
                allowNow
                onConfirm={(val) => {
                  updateData({ pickupDate: val.date, pickupTime: val.time });
                  setShowDeparturePicker(false);
                }}
                onCancel={() => setShowDeparturePicker(false)}
              />
            )}
          </div>
        </div>

        {/* Return Date & Time (Conditional) */}
        {data.journeyType === 'round-trip' && (
          <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1" ref={returnRef}>
            <label className="font-host text-[13px] font-medium text-[#757575]">
              Return Date & Time <span className="text-[#FF6A1A]">*</span>
            </label>
            <div className="relative flex items-center h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white divide-x divide-[#E0E0E0]">
              <button
                type="button"
                onClick={() => {
                  setShowReturnPicker(!showReturnPicker);
                  setShowDeparturePicker(false);
                }}
                className="flex-1 flex items-center justify-between px-4 h-full"
              >
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-[#9E9E9E]" />
                  <span className="font-host text-[14px] text-[#1A1A1A]">
                    {formatDateLabel(data.returnDate)}
                  </span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReturnPicker(!showReturnPicker);
                  setShowDeparturePicker(false);
                }}
                className="w-[120px] flex items-center justify-center gap-2 h-full shrink-0"
              >
                <Clock size={16} className="text-[#9E9E9E]" />
                <span className="font-host text-[14px] text-[#1A1A1A]">
                  {formatTimeLabel(returnValue)}
                </span>
              </button>

              {showReturnPicker && (
                <DateTimePicker
                  value={returnValue}
                  minDateTime={departureValue}
                  onConfirm={(val) => {
                    updateData({ returnDate: val.date, returnTime: val.time });
                    setShowReturnPicker(false);
                  }}
                  onCancel={() => setShowReturnPicker(false)}
                />
              )}
            </div>
          </div>
        )}

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
        {/* Passengers */}
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Passengers <span className="text-[#FF6A1A]">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              min="1"
              max="16"
              placeholder="e.g. 2"
              value={data.passengers}
              onChange={(e) => updateData({ passengers: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>
        </div>

        {/* Luggage */}
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">Luggage</label>
          <div className="relative">
            <input
              type="number"
              min="0"
              placeholder="e.g. 2"
              value={data.luggage}
              onChange={(e) => updateData({ luggage: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>
        </div>
        {/* Payment Status */}
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Payment Status <span className="text-[#FF6A1A]">*</span>
          </label>

          <div className="relative">
            <select
              value={data.paymentStatus}
              onChange={(e) =>
                updateData({
                  paymentStatus: e.target.value as TripDetailsData['paymentStatus'],
                })
              }
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 pr-10 font-host text-[14px] text-[#1A1A1A] appearance-none focus:outline-none focus:border-[#FF6A1A] cursor-pointer"
            >
              <option value="pre_paid">Pre-paid</option>
              <option value="cash">Cash</option>
              <option value="account_transfer">Account Transfer</option>
              <option value="paid">Paid</option>
            </select>

            <ChevronDown
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A] pointer-events-none"
              size={16}
            />
          </div>
        </div>
      </div>
      
    </div>
  );
};
